import React, { type ReactElement } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { mapValues } from 'lodash';
import {
  BooleanParam,
  DelimitedArrayParam,
  DelimitedNumericArrayParam,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault
} from 'use-query-params';
import Card from 'atom-components/card';
import TablePagination from 'atom-components/table-pagination';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import {
  nonReviewMarkStatuses,
  reviewMarkStatuses
} from 'helpers/domain-specific/mark-status.helpers';
import {
  defaultPage,
  defaultPaginationFilters,
  generatePaginationFilters
} from 'helpers/pagination.helpers';
import { toArrayOfNumbers, toArrayOfStrings, toYesNoOrBoth } from 'helpers/query-params.helpers';
import {
  defaultSortingFilter,
  fromSortingState,
  isColumnSorted,
  sortDirection,
  toSortingState
} from 'helpers/sorting.helpers';
import { firstTimeInYear, lastTimeInYear } from 'helpers/year.helpers';
import MarkingPageLayout from 'pages/marking/layouts/marking-page-layout';
import { type FilterFormValues } from 'pages/marking/subpages/marker-dashboard-tab/filter-form/filter-form.schema';
import { useMe } from 'services/assist-api/entities/me/me';
import { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import { useMarkingTasks } from 'services/assist-api/entities/test-instance-response-marker/marking-tasks/hooks';
import type { TestInstanceResponseInputType } from 'services/assist-api/entities/test-instance-response/types';
import { isInitialLoad } from 'utils/check-query-status';
import FilterForm from './filter-form';
import MarkerDashboardTable from './marker-dashboard-table';

const defaultTableFilters = {
  nsn: withDefault(StringParam, ''),
  responseId: withDefault(StringParam, ''),
  markId: withDefault(StringParam, ''),
  testIds: withDefault(DelimitedNumericArrayParam, []),
  modes: withDefault(DelimitedArrayParam, []),
  statuses: withDefault(DelimitedArrayParam, []),
  markerOrganisationUserIds: withDefault(DelimitedNumericArrayParam, []),
  review: withDefault(StringParam, ''),
  comment: withDefault(StringParam, ''),
  markingStarted: withDefault(StringParam, ''),
  allocatedToMeOnly: withDefault(BooleanParam, false)
};

export default function MarkerDashboardTab(): ReactElement {
  const [tableFilters, setTableFilters] = useQueryParams(defaultTableFilters);
  const [sortingFilter, setSortingFilter] = useQueryParam('sorting', defaultSortingFilter);
  const [paginationFilters, setPaginationFilters] = useQueryParams(defaultPaginationFilters);

  // Get the current user's details and organisation from the cache. We should have an earlier check
  // to ensure that the user has joined and is switched to an organisation by this point:
  const meQuery = useMe();

  function generateMarkingTaskFilters(): EntityFilters {
    const filters: EntityFilters = {};

    // Start table row filters.
    if (tableFilters.nsn) {
      filters['testInstanceResponse.user.user.identifier'] = tableFilters.nsn;
    }
    if (tableFilters.responseId) {
      filters.testInstanceResponse = tableFilters.responseId;
    }
    if (tableFilters.markId) {
      filters['activeResponseMarks.id'] = tableFilters.markId;
    }
    if (tableFilters.testIds.length > 0) {
      filters['testInstanceResponse.testInstance.test'] = toArrayOfNumbers(tableFilters.testIds);
    }
    if (tableFilters.modes.length > 0) {
      filters['testInstanceResponse.inputTypes'] = toArrayOfStrings(tableFilters.modes);
    }
    if (tableFilters.statuses.length > 0) {
      filters['activeResponseMarks.status'] = toArrayOfStrings(
        tableFilters.statuses.filter((s) => s !== MarkStatus.Assigned)
      );
    }

    // Ignore the markerOrganisationUserIds filter if the user opts to set the allocated to me
    // only filter. The marker filter should also be disabled in the UI in this situation:
    if (tableFilters.allocatedToMeOnly) {
      filters.user = tableFilters.allocatedToMeOnly ? meQuery?.data?.customerUser?.id : undefined;
    } else {
      filters.user = toArrayOfNumbers(tableFilters.markerOrganisationUserIds);
    }

    // Expected to take precedence over status filter:
    if (tableFilters.review) {
      filters['activeResponseMarks.status'] =
        tableFilters.review === 'Y' ? reviewMarkStatuses : nonReviewMarkStatuses();
    }

    if (tableFilters.comment === 'Y') {
      filters['exists[activeResponseMarks.comment]'] = true;
      filters['exists[activeResponseMarks]'] = true;
    }
    if (tableFilters.comment === 'N') {
      filters['exists[activeResponseMarks.comment]'] = false;
      filters['exists[activeResponseMarks]'] = true;
    }

    if (tableFilters.markingStarted === 'Y') {
      filters['exists[responseMarks]'] = true;
    }
    if (tableFilters.markingStarted === 'N') {
      // Since assigned marks don't exist, use exists[activeResponseMarks]=false to fetch marking
      // tasks with the Assigned status:
      filters['exists[responseMarks]'] = false;
    }
    // End table row filters.

    // Start column sorting filters.
    if (isColumnSorted('nsn', sortingFilter)) {
      filters['order[testInstanceResponse.user.user.identifier]'] = sortDirection(
        'nsn',
        sortingFilter
      );
    }
    if (isColumnSorted('responseId', sortingFilter)) {
      filters['order[testInstanceResponse.id]'] = sortDirection('responseId', sortingFilter);
    }
    if (isColumnSorted('markId', sortingFilter)) {
      filters['order[activeResponseMarks.id]'] = sortDirection('markId', sortingFilter);
    }
    if (isColumnSorted('test', sortingFilter)) {
      filters['order[testInstanceResponse.testInstance.test.name]'] = sortDirection(
        'test',
        sortingFilter
      );
    }
    if (isColumnSorted('mode', sortingFilter)) {
      filters['order[testInstanceResponse.inputTypes]'] = sortDirection('mode', sortingFilter);
    }
    if (isColumnSorted('markStatus', sortingFilter)) {
      filters['order[activeResponseMarks.status]'] = sortDirection('markStatus', sortingFilter);
    }
    // Sort by username (displayed in tooltip), since we can't reliably sort by full name:
    if (isColumnSorted('marker', sortingFilter)) {
      filters['order[user.user.username]'] = sortDirection('marker', sortingFilter);
    }
    // Note: the "review" column is not sortable due to limitations relating to grouping multiple
    // statuses in the API.
    if (isColumnSorted('comment', sortingFilter)) {
      filters['order[activeResponseMarks.comment]'] = sortDirection('comment', sortingFilter);
      filters['order[activeResponseMarks.id]'] = sortDirection('comment', sortingFilter);
    }
    // End column sorting filters.

    return filters;
  }

  const currentYear = new Date().getFullYear().toString();
  const markingTasksQuery = useMarkingTasks(
    {
      filters: {
        'testInstanceResponse.testInstance.customer': meQuery?.data?.customer?.id,
        'testInstanceResponse.testInstance.startDate[after]': firstTimeInYear(currentYear),
        'testInstanceResponse.testInstance.startDate[before]': lastTimeInYear(currentYear),
        ...generatePaginationFilters(paginationFilters.pageSize, paginationFilters.page),
        ...generateMarkingTaskFilters()
      }
    },
    {
      enabled:
        !!(
          paginationFilters?.pageSize &&
          paginationFilters?.page &&
          tableFilters.allocatedToMeOnly !== undefined
        ) &&
        !!meQuery.data?.customerUser?.id &&
        !!meQuery.data?.customer?.id
    }
  );

  const isLoading =
    !tableFilters ||
    !meQuery?.data?.customerUser ||
    !meQuery?.data?.customer ||
    isInitialLoad(markingTasksQuery);

  const { isError } = markingTasksQuery;

  return (
    <MarkingPageLayout>
      <Card>
        <Card.Header>
          <Card.Heading level={3}>Marker Dashboard</Card.Heading>
        </Card.Header>
        <Card.Body>
          <FilterForm
            defaultValuesFromURLParams={{
              nsn: tableFilters.nsn,
              responseId: tableFilters.responseId,
              markId: tableFilters.markId,
              testIds: toArrayOfNumbers(tableFilters.testIds),
              modes: toArrayOfStrings(tableFilters.modes) as TestInstanceResponseInputType[],
              statuses: toArrayOfStrings(tableFilters.statuses) as MarkStatus[],
              markerOrganisationUserIds: toArrayOfNumbers(tableFilters.markerOrganisationUserIds),
              review: toYesNoOrBoth(tableFilters.review),
              comment: toYesNoOrBoth(tableFilters.comment),
              markingStarted: toYesNoOrBoth(tableFilters.markingStarted),
              allocatedToMeOnly: tableFilters.allocatedToMeOnly
            }}
            onSubmit={(formValues: FilterFormValues): void => {
              setTableFilters({ ...formValues });
              setPaginationFilters({ page: defaultPage });
            }}
            onReset={(): void => {
              setTableFilters(mapValues(defaultTableFilters, () => undefined));
              setPaginationFilters({ page: defaultPage });
            }}
          />
          {!isLoading && isError && <LoadingErrorAlert queries={[markingTasksQuery]} />}
          <MarkerDashboardTable
            markingTasks={markingTasksQuery.data?.json || []}
            initialSorting={toSortingState(sortingFilter)}
            onSort={(sorting: SortingState): void => setSortingFilter(fromSortingState(sorting))}
            isLoading={isLoading}
          />
        </Card.Body>
        <Card.Footer>
          <TablePagination
            query={markingTasksQuery}
            onChange={({ pageSize, page }): void => setPaginationFilters({ pageSize, page })}
          />
        </Card.Footer>
      </Card>
    </MarkingPageLayout>
  );
}
